import {
  Boundary,
  Button,
  Icon,
  Menu,
  MenuItem,
  OverflowList,
  Popover,
  Tag,
  Tooltip,
} from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import DataTableFilterLabel from 'Components/Generic/AdvancedDataTable/DataTableFilterLabel';
import ConditionalWrapper from 'Lib/hoc/ConditionalWrapper';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { List } from 'react-virtualized';
import { isItemInactive } from 'Util';
import styles from './BOSMultiselect.module.css';

const BOSFilterMultiselect = ({
  labelText,
  listData = [],
  onSelectionUpdate,
  idField = 'id',
  labelField = 'name',
  labelRenderer,
  searchField,
  searchFunction,
  tooltipField,
  placeholder = 'Select...',
  defaultSelectedItems,
  useKeyValueFields = false,
  autofocus,
  showClearButton = false,
  viewOnly = false,
  isHeader = false,
  maxWidth,
}) => {
  const [selectedItems, setSelectedItems] = useState(
    defaultSelectedItems || [],
  );
  const [fieldNames] = useState(
    useKeyValueFields
      ? {
          idField: 'key',
          labelField: 'value',
          ...(tooltipField && { tooltipField }),
        }
      : {
          idField,
          labelField,
          searchField,
          ...(tooltipField && { tooltipField }),
        },
  );

  useEffect(() => {
    onSelectionUpdate(selectedItems);
  }, [selectedItems]);

  const itemListRenderer = ({
    filteredItems,
    itemsParentRef,
    menuProps,
    renderItem,
    query,
  }) => {
    let height = filteredItems.length * 30;
    height = height <= 300 ? height : 300;
    return (
      <Menu role='listbox' ulRef={itemsParentRef} {...menuProps}>
        {filteredItems.length === 0 && (
          <MenuItem
            className={styles.no_content}
            text={query ? `No items matching '${query}'...` : 'No items'}
            icon={'blank'}
          />
        )}
        <List
          className={styles.list}
          height={height}
          rowHeight={30}
          rowCount={filteredItems.length}
          overscanRowCount={50}
          width={400}
          rowRenderer={getRowRenderer(filteredItems, renderItem)}
        />
      </Menu>
    );
  };

  const getRowRenderer = (filteredItems, renderItem) => {
    return ({ index, isScrolling, key, style }) => {
      let item = filteredItems[index];
      return (
        <div className={styles.row} key={key} style={style}>
          {renderItem(item)}
        </div>
      );
    };
  };

  const itemRenderer = (item, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    return (
      <MenuItem
        className={
          isItemInactive(item[fieldNames.labelField]) ? styles.inactive : ''
        }
        active={modifiers.active}
        key={item[fieldNames.idField]}
        icon={isItemSelected(item) ? 'tick' : 'blank'}
        shouldDismissPopover={false}
        text={item[fieldNames.labelField]}
        onClick={handleClick}
      />
    );
  };

  const getClearIcon = (item) => {
    return (
      <Icon
        style={{ cursor: 'pointer' }}
        icon={'small-cross'}
        onClick={() => deselectItem(item)}
      />
    );
  };

  const itemTagRenderer = (item) => {
    return (
      <Fragment key={item[fieldNames.idField]}>
        <ConditionalWrapper
          condition={fieldNames?.tooltipField}
          wrapper={(children) => (
            <Tooltip content={item[fieldNames.tooltipField]}>
              {children}
            </Tooltip>
          )}>
          <Tag
            minimal
            className={
              viewOnly ? styles.overflow_tag : styles.overflow_tag_with_clear
            }
            rightIcon={!viewOnly && getClearIcon(item)}>
            {labelRenderer ? labelRenderer(item) : item[fieldNames.labelField]}
          </Tag>
        </ConditionalWrapper>
      </Fragment>
    );
  };

  const overflowRenderer = (itemList) => {
    let menu = (
      <div className={styles.tag_box} style={{ padding: 4 }}>
        {itemList.map(itemTagRenderer)}
      </div>
    );

    return (
      <Popover content={menu} placement={'bottom'} interactionKind='hover'>
        <Icon className={'bp5-tag bp5-minimal'} icon={'more'} />
      </Popover>
    );
  };

  const onSelectionChange = (item) => {
    if (isItemSelected(item)) {
      deselectItem(item);
    } else {
      selectItem(item);
    }
  };

  const getSelectedItemIndex = (item) => {
    return selectedItems.findIndex(
      (selectedItem) =>
        selectedItem[fieldNames.idField] === item[fieldNames.idField],
    );
  };

  const isItemSelected = (item) => {
    return getSelectedItemIndex(item) >= 0;
  };

  const selectItem = (itemToSelect) => {
    let newSelectedItems = [
      ...listData.filter(
        (item) => isItemSelected(item) || item === itemToSelect,
      ),
    ];
    setSelectedItems(newSelectedItems);
  };

  const deselectItem = (item) => {
    setSelectedItems(
      selectedItems.filter((selectedItem) => selectedItem !== item),
    );
  };

  const filterPredicate = (query, item) => {
    let fieldToSearchBy = fieldNames.searchField || fieldNames.labelField;
    let itemSearchText = searchFunction
      ? searchFunction(item)
      : item[fieldToSearchBy];
    return itemSearchText?.toLowerCase().includes(query.toLowerCase());
  };

  let sortedArray = [
    ...listData.filter((item) => isItemSelected(item)),
    ...listData.filter((item) => !isItemSelected(item)),
  ];

  const clearButton = showClearButton &&
    !viewOnly &&
    selectedItems?.length > 0 && (
      <Button
        className={styles.clear_button}
        minimal
        icon={'cross'}
        onClick={() => {
          setSelectedItems([]);
        }}
      />
    );

  let sortedSelection = selectedItems.sort((a, b) =>
    a[fieldNames.labelField].localeCompare(b[fieldNames.labelField]),
  );

  return (
    <div
      className={
        isHeader
          ? styles.filter_label_header_box
          : styles.filter_label_input_box
      }
      style={maxWidth && !isHeader ? { maxWidth: maxWidth } : {}}>
      {labelText && <DataTableFilterLabel label={labelText} />}
      <div
        className={
          !viewOnly
            ? `bp5-input ${styles.filter_suggest_selection_box}`
            : styles.tag_box
        }>
        {selectedItems.length > 0 && (
          <OverflowList
            collapseFrom={viewOnly ? Boundary.END : Boundary.START}
            className={styles.overflow_list}
            items={sortedSelection}
            overflowRenderer={overflowRenderer}
            visibleItemRenderer={itemTagRenderer}
          />
        )}
        {!viewOnly && (
          <Suggest
            className={`${styles.filter_suggest_input}  ${
              selectedItems?.length && styles.filter_suggest_input_narrow
            }`}
            closeOnSelect={false}
            itemListRenderer={itemListRenderer}
            itemRenderer={itemRenderer}
            inputValueRenderer={() => ' '}
            inputProps={{
              placeholder: selectedItems?.length ? ' ' : placeholder,
              ...(autofocus &&
                !defaultSelectedItems?.length && { autoFocus: true }),
            }}
            items={sortedArray}
            itemsEqual={fieldNames.idField}
            itemPredicate={filterPredicate}
            onItemSelect={onSelectionChange}
            popoverProps={{ minimal: true }}
            popoverContentProps={{
              className: styles.filter_selection_menu_popover,
            }}
          />
        )}
        {clearButton}
      </div>
    </div>
  );
};

BOSFilterMultiselect.propTypes = {
  labelText: PropTypes.string,
  listData: PropTypes.array.isRequired,
  onSelectionUpdate: PropTypes.func,
  idField: PropTypes.string,
  labelField: PropTypes.string,
  labelRenderer: PropTypes.func,
  searchField: PropTypes.string,
  searchFunction: PropTypes.func,
  tooltipField: PropTypes.string,
  placeholder: PropTypes.string,
  fill: PropTypes.bool,
  defaultSelectedItems: PropTypes.array,
  maxWidth: PropTypes.number,
  useKeyValueFields: PropTypes.bool,
  autofocus: PropTypes.bool,
  showClearButton: PropTypes.bool,
  viewOnly: PropTypes.bool,
  isHeader: PropTypes.bool,
};

export default BOSFilterMultiselect;
